import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { t } from '@web-solutions/module-localization';
import { useRemoteConfig } from '@web-solutions/base-app/hooks/use-remote-config';

import { GooglePayButton, PaymentSystem } from '@web-solutions/react-billing';
import { selectPaymentSystem } from '@web-solutions/core/store/billing/selectors';
import { enrichStripeIntroProduct } from '@web-solutions/core/utils/stripe-payment';

import classes from './style.module.scss';

const GooglePay = ({
  activeProduct,
  className,
  onGooglePayClick,
  onGooglePaySubmit,
  onRequireEmail,
  onAvailabilityChange,
}) => {
  const email = useSelector((state) => state.profile.email);
  const paymentSystem = useSelector(selectPaymentSystem);
  const { applePayMerchantName } = useRemoteConfig();

  // What the native sheet prints next to the amount ("Order summary"). The
  // remote-config merchant name wins: `core.appName` is the localization key and
  // still carries the original brand on funnels that were renamed.
  const merchantName = applePayMerchantName || t('core.appName');

  // Stripe only. Every other system either draws its own Google Pay button into
  // the container this modal provides for SDK-rendered wallet buttons
  // (Solidgate) or has no Google Pay at all.
  const isStripe =
    paymentSystem === PaymentSystem.STRIPE || Boolean(activeProduct?.stripeId);

  if (!isStripe) {
    return null;
  }

  // Stripe charges the intro one-time amount today and then the subscription.
  // Without enrichment the button would read the subscription price and show the
  // wrong total in the native sheet.
  const introAmount = activeProduct?.trialPriceAmount ? Number(activeProduct.trialPriceAmount) : (activeProduct?.amount ? Number(activeProduct.amount) : (activeProduct?.priceAmount ? Number(activeProduct.priceAmount) : undefined));
  const productForGooglePay =
    enrichStripeIntroProduct(activeProduct, introAmount) || activeProduct;

  const handleSubmit = (formData) => {
    onGooglePaySubmit({ email, ...formData });
  };

  return (
    <GooglePayButton
      product={productForGooglePay}
      label={merchantName}
      className={classNames(classes.btn, className)}
      onClick={onGooglePayClick}
      onSubmit={handleSubmit}
      email={email}
      onRequireEmail={onRequireEmail}
      onAvailabilityChange={onAvailabilityChange}
    />
  );
};

GooglePay.propTypes = {
  activeProduct: PropTypes.object.isRequired,
  className: PropTypes.any,
  onGooglePayClick: PropTypes.func,
  onGooglePaySubmit: PropTypes.func.isRequired,
  onRequireEmail: PropTypes.func,
  onAvailabilityChange: PropTypes.func,
};

GooglePay.defaultProps = {
  className: null,
  onGooglePayClick: null,
  onRequireEmail: null,
  onAvailabilityChange: null,
};

export default GooglePay;
